import { Template } from '../../core/template.schemas';
import { EntitySlotSelectionMap, SelectorSelectionMap, AttributeOverrideMap } from '../types/PostGenerationTypes';
import { SlotBinding } from './TemplateAnalysisService';
import { ResolveContext, resolveStaticOrDynamic, resolveTextContent, resolveImageSource } from '../../core/runtime/resolve';

/**
 * Service for generating posts with populated entity data using slot system
 */
export class PostGenerationService {
  /**
   * Generates a post by populating template with entity data from slots
   */
  static generatePost(
    template: Template,
    slotBindings: SlotBinding[],
    entitySlots: EntitySlotSelectionMap,
    selectorSelections: SelectorSelectionMap,
    manualOverrides: AttributeOverrideMap = {}
  ): PostGenerationResult {
    const populatedTemplate = this.cloneTemplate(template);

    const selectedSlots: { [slotId: string]: any } = Object.values(entitySlots).reduce((acc, slot) => {
      if (slot.selectedInstance && slot.slotId) acc[slot.slotId] = slot.selectedInstance.data;
      return acc;
    }, {} as Record<string, any>);

    const selectedSelectors: { [selectorId: string]: any } = Object.values(selectorSelections || {}).reduce((acc, sel: any) => {
      acc[sel.selectorId] = sel.selectedValue ?? sel.defaultValue ?? undefined;
      return acc;
    }, {} as Record<string, any>);

    // Construct flat overrides map for resolver
    const overrides: Record<string, any> = {};
    const manualSlotOverrides: Record<string, Record<string, any>> = {};
    
    // Map manual slot overrides to resolver context
    console.log('[PostGen] Generate called. ManualOverrides:', manualOverrides ? Object.keys(manualOverrides) : 'undefined');
    if (manualOverrides) {
        Object.entries(manualOverrides).forEach(([k, v]) => console.log(`[PostGen] Key: ${k}, Val:`, v));
    }

    Object.entries(manualOverrides).forEach(([slotKey, slotOverrides]) => {
      const slot = entitySlots[slotKey];
      console.log(`[PostGen] Processing slotKey: ${slotKey}, found slot:`, !!slot, 'slotId:', slot?.slotId);
      if (slot && slot.slotId) {
        // Initialize map for this slot ID
        if (!manualSlotOverrides[slot.slotId!]) {
          manualSlotOverrides[slot.slotId!] = {};
        }
        
        Object.entries(slotOverrides).forEach(([attrPath, value]) => {
          manualSlotOverrides[slot.slotId!][attrPath] = value;
        });
      }
    });

    const resolveContext: ResolveContext = {
      selected: { slots: selectedSlots, selectors: selectedSelectors },
      overrides: overrides,
      manualSlotOverrides: manualSlotOverrides,
      templateSlots: template.slots || [],
    };

    const populationResults = slotBindings.map(binding => 
      this.populateSlotBinding(template, binding, resolveContext)
    );

    const failed = populationResults.filter(r => !r.success);

    if (failed.length > 0) {
      console.warn('Failed bindings detected', failed.map(f => f.error));
    }

    const entities = Object.values(entitySlots)
      .map(slot => slot.selectedInstance)
      .filter((instance): instance is import('../types/PostGenerationTypes').EntityInstance => instance !== null);

    const entityContexts = Object.values(entitySlots)
      .filter(slot => slot.selectedInstance)
      .map(slot => ({
        slotKey: slot.slotKey,
        entityType: slot.entityType,
        label: slot.selectedInstance?.label,
        data: slot.selectedInstance?.data,
      }));

    const textElements = populatedTemplate.elements
      .filter((element): element is import('../../core/template.schemas').TextElement => element.type === 'text')
      .map((element, index) => ({
        name: element.name || `text_${index + 1}`,
        value: String(resolveTextContent(element.content, resolveContext) ?? '').trim(),
      }))
      .filter((element) => element.value.length > 0);

    return {
      populatedTemplate,
      success: failed.length === 0,
      populationResults,
      errors: failed.map(r => r.error).filter(Boolean) as string[],
      entities,
      entityContexts,
      textElements,
    };
  }

  /**
   * Populates a single slot binding in the template
   */
  private static populateSlotBinding(
    template: Template,
    binding: SlotBinding,
    resolveContext: ResolveContext
  ): BindingPopulationResult {
    try {
      // Find target element
      const element = template.elements.find(el => el.id === binding.elementId);
      if (!element) {
        return {
          bindingId: binding.id,
          success: false,
          error: `Element ${binding.elementId} not found`
        };
      }

      let spec: any = (element as any)[binding.elementAttribute];
      if (binding.elementAttribute.includes('.')) {
        const parts = binding.elementAttribute.split('.');
        let target: any = element;
        for (let i = 0; i < parts.length; i++) {
          target = target?.[parts[i]];
        }
        spec = target;
      }

      let resolved: any;
      if (binding.mediaType === 'text' || binding.mediaType === 'date' || binding.mediaType === 'int' || binding.mediaType === 'float') {
        resolved = resolveTextContent(spec, resolveContext);
      } else if (binding.mediaType === 'image') {
        resolved = resolveImageSource(spec, resolveContext);
      } else {
        resolved = resolveStaticOrDynamic(spec, resolveContext);
      }

      if (resolved === undefined || resolved === null || (binding.mediaType === 'image' && typeof resolved !== 'string')) {
        return {
          bindingId: binding.id,
          success: false,
          error: `Value not resolvable for ${binding.elementAttribute}`
        };
      }

      return {
        bindingId: binding.id,
        success: true,
        populatedValue: resolved
      };

    } catch (error) {
      return {
        bindingId: binding.id,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Creates a deep clone of the template
   */
  private static cloneTemplate(template: Template): Template {
    return JSON.parse(JSON.stringify(template));
  }
}

/**
 * Result of post generation process
 */
export interface PostGenerationResult {
  /** Template with populated values */
  populatedTemplate: Template;
  /** Whether generation was successful */
  success: boolean;
  /** Results for each binding population */
  populationResults: BindingPopulationResult[];
  /** Error messages from failed populations */
  errors: string[];
  /** Selected entities used in generation */
  entities: import('../types/PostGenerationTypes').EntityInstance[];
  /** Entity context with slot metadata for downstream systems like AI caption generation */
  entityContexts: {
    slotKey: string;
    entityType: string;
    label?: string;
    data: any;
  }[];
  /** Resolved text elements from the generated post */
  textElements: {
    name: string;
    value: string;
  }[];
}

/**
 * Result of populating a single binding
 */
export interface BindingPopulationResult {
  /** Binding identifier */
  bindingId: string;
  /** Whether population was successful */
  success: boolean;
  /** The populated value (if successful) */
  populatedValue?: any;
  /** Error message (if failed) */
  error?: string;
}
